"use client";

import { useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { type ActiveFilter, type FilterFieldDef } from "./types";
import { FilterRow, resolveOp, resolveDefaultVal } from "./filter-row";
import { Icon } from "@/components/ui/icon";

export function FilterPanel({
  fields,
  filters,
  onFiltersChange,
}: {
  fields: FilterFieldDef[];
  filters: ActiveFilter[];
  onFiltersChange: (filters: ActiveFilter[]) => void;
}) {
  const usedFieldKeys = filters.map((f) => f.field);
  const availableFields = fields.filter((f) => !usedFieldKeys.includes(f.key));

  const addFilter = useCallback(() => {
    if (availableFields.length === 0) return;
    const field = availableFields[0];
    onFiltersChange([
      ...filters,
      {
        field: field.key,
        operator: resolveOp(field.type),
        value: resolveDefaultVal(field),
      },
    ]);
  }, [filters, availableFields, onFiltersChange]);

  const updateFilter = useCallback(
    (index: number, updated: ActiveFilter) => {
      onFiltersChange(filters.map((f, i) => (i === index ? updated : f)));
    },
    [filters, onFiltersChange],
  );

  const removeFilter = useCallback(
    (index: number) => {
      onFiltersChange(filters.filter((_, i) => i !== index));
    },
    [filters, onFiltersChange],
  );

  const hasFilters = filters.length > 0;

  const addFilterBtn = (
    <button
      type="button"
      onClick={addFilter}
      className={cn(
        buttonVariants({ variant: "secondary", size: "xs" }),
        "h-6 gap-1 px-2 text-contrast/70",
      )}
    >
      <Icon name="IconPlusSmall" size={12} />
      Add filter
    </button>
  );

  return (
    <div className="min-w-[min(280px,calc(100vw-2rem))] max-w-full lg:max-w-[min(520px,calc(100vw-2rem))] overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2.5">
        <span className="text-[13px] font-medium text-contrast/50">
          Filters
        </span>
        {hasFilters ? (
          <div className="flex items-center gap-2">
            {availableFields.length > 0 && addFilterBtn}
            <button
              type="button"
              onClick={() => onFiltersChange([])}
              className={cn(
                buttonVariants({ variant: "ghost", size: "xs" }),
                "font-medium text-contrast/30 hover:text-contrast/60",
              )}
            >
              Clear all
            </button>
          </div>
        ) : availableFields.length > 0 ? (
          addFilterBtn
        ) : null}
      </div>

      <div className="h-px bg-contrast/8 mx-3" />

      <AnimatePresence mode="popLayout">
        {hasFilters ? (
          <motion.div
            key="filter-rows"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="px-3 py-2 min-w-0"
          >
            <AnimatePresence initial={false}>
              {filters.map((filter, idx) => (
                <FilterRow
                  key={filter.field}
                  filter={filter}
                  fields={fields}
                  usedFieldKeys={usedFieldKeys}
                  onUpdate={(updated) => updateFilter(idx, updated)}
                  onRemove={() => removeFilter(idx)}
                />
              ))}
            </AnimatePresence>
          </motion.div>
        ) : (
          <motion.div
            key="empty-state"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="px-3 py-3 text-[13px] text-contrast/25"
          >
            No filters applied
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
